import DevTools from "@/components/devtools";
import Wrapper from "@/components/layout/wrapper";
import { StaffSessionProvider, useStaffSession } from "@/contexts/staff-session/staff-session.context";
import ThemeContextProvider from "@/contexts/theme/theme.context-provider";
import { useCookie } from "@/features/shared/cookies/use-cookie.hook";
import useGetCipUser from "@/hooks/use-get-cip-user.hook";
import useOnLogout from "@/hooks/use-on-logout.hook";
import i18next from "@/i18n";
import CriticalError from "@/routes/@errors/500";
import Auth from "@/routes/auth";
import { getClicknparkServicesToken } from "@/services/cip.services";
import { setCentralCipTokenHeader } from "@/services/config/api";
import { legacySessionToken } from "@/services/staff.services";
import queryClient from "@/services/config/query-client";
import { AppError, HandledError, handleError } from "@/services/helpers/clicknpark-errors.helpers";
import { Button, Spinner } from "@EspaceParkQC/sdk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider, useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { appRouter } from "./router";

// Removes auto-zoom on iPhone
if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
  const viewport = document.querySelector('meta[name="viewport"]');
  viewport?.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1");
}

/** Staff session: the user comes from the clicknpark API, and requests read the staff token themselves. */
function StaffComponent(): React.ReactElement {
  const logout = useOnLogout();
  const { t } = useTranslation("main");
  const { user, error } = useStaffSession();

  if (error) {
    const handled = handleError(error);
    return (
      <CriticalError
        title={handled.formatted.title}
        description={handled.formatted.description}
        action={<Button onClick={() => void logout()}>{t("session.logout")}</Button>}
      />
    );
  }

  if (!user) {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.loading-user-info")}</p>
      </Wrapper>
    );
  }

  return <RouterProvider router={appRouter} />;
}

function SessionSwitch(): React.ReactElement {
  const { mode } = useStaffSession();
  const { t } = useTranslation("main");

  if (mode === "checking") {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.authenticating")}</p>
      </Wrapper>
    );
  }

  return mode === "staff" ? <StaffComponent /> : <Component />;
}

/** central-server session, kept when a handoff fails, and the sign-in page when nobody is signed in. */
function Component(): React.ReactElement {
  const logout = useOnLogout();
  const { t } = useTranslation("main");

  const { set: setServicesToken } = useCookie<string>("cnp_services_token");
  const { value: cookieValue } = useCookie<string>("cnp_cip_token");
  const cipToken = legacySessionToken(cookieValue);
  const [headersAdded, setHeadersAdded] = useState(false);
  const [tokenError, setTokenError] = useState<HandledError | undefined>();
  const [loadingToken, setLoadingToken] = useState(true);
  const { loading: loadingUser, error } = useGetCipUser();

  useEffect(() => {
    if (cipToken) {
      setCentralCipTokenHeader(cipToken);
      setHeadersAdded(true);
    }
  }, [cipToken]);

  useEffect(() => {
    if (!headersAdded) return;

    async function fetchToken(): Promise<void> {
      try {
        const { token, expiry } = await getClicknparkServicesToken();

        if (token && expiry) {
          setServicesToken(token, { expires: new Date(expiry) });
          setLoadingToken(false);
        } else {
          setTokenError(handleError(new AppError("Failed to fetch token", "Token not found")));
          setLoadingToken(false);
        }
      } catch (error) {
        const handledError = handleError(error);
        setTokenError(handledError);
        setLoadingToken(false);
      }
    }

    void fetchToken();
  }, [headersAdded, setServicesToken]);

  useEffect(() => {
    if (error) {
      if (isAxiosError(error) && error.code === "ERR_NETWORK") {
        // setServerDown(true);
      } else if (isAxiosError(error) && error.code === "ECONNABORTED") {
        // setServerDown(true);
      } else {
        void logout();
      }
    }
  }, [error, logout]);

  if (tokenError) {
    return (
      <CriticalError
        title={tokenError.formatted.title}
        description={tokenError.formatted.description}
        action={<Button onClick={() => void logout()}>{t("session.logout")}</Button>}
      />
    );
  } else if (!cipToken) {
    return <Auth />;
  } else if (loadingUser) {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.loading-user-info")}</p>
      </Wrapper>
    );
  } else if (loadingToken) {
    return (
      <Wrapper className="items-center justify-center gap-4">
        <Spinner className="text-primary" />
        <p>{t("session.authenticating")}</p>
      </Wrapper>
    );
  } else {
    return <RouterProvider router={appRouter} />;
  }
}

export default function Root(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <I18nextProvider i18n={i18next}>
          <ThemeContextProvider>
            <StaffSessionProvider>
              <SessionSwitch />
            </StaffSessionProvider>
            <DevTools />
          </ThemeContextProvider>
        </I18nextProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
